import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialNormalOutput, materialPointOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {z} from "zod/v4-mini"
import {vec3} from "#material/types/vector"

export interface TexCoordSockets extends MaterialNodeSockets {
    inputs: {}
    settings: {
        camera: "point" // found in inputs: -; found in settings: vector,output; inferred from data: float3,null
        coordinateType: "string"
        fromInstancer: "boolean" // found in inputs: -; found in settings: bool; inferred from data: boolean
        generated: "point" // found in inputs: -; found in settings: vector,output; inferred from data: float3,null
        normal: "normal" // found in inputs: -; found in settings: vector,output; inferred from data: float3,null
        object: "point" // found in inputs: -; found in settings: vector,output; inferred from data: float3,null
        reflection: "normal" // found in inputs: -; found in settings: vector,output; inferred from data: float3,null
        uv: "point" // found in inputs: -; found in settings: vector,output; inferred from data: float3,null
        window: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
    }
    outputs: {
        camera: "point"
        generated: "point"
        normal: "normal"
        object: "point"
        reflection: "normal"
        uv: "point"
        window: "point"
    }
}

export const texCoordDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeTexCoord,
    },
    nodeClass: "TexCoord",
    ui: {
        label: "Texture Coordinate",
        color: "#4987af",
        items: [
            {name: "generated", valueType: "point", label: "Generated", output: true},
            {name: "normal", valueType: "normal", label: "Normal", output: true},
            {name: "uv", valueType: "point", label: "UV", output: true},
            {name: "object", valueType: "point", label: "Object", output: true},
            {name: "camera", valueType: "point", label: "Camera", output: true},
            {name: "reflection", valueType: "normal", label: "Reflection", output: true},
            {name: "window", valueType: "point", label: "Window", output: true},
        ],
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({
            camera: materialPointOutput,
            generated: materialPointOutput,
            normal: materialNormalOutput,
            object: materialPointOutput,
            reflection: materialNormalOutput,
            uv: materialPointOutput,
            window: materialPointOutput,
        }),
        parameters: z.strictObject({
            inputs: z.strictObject({}),
            settings: z.strictObject({
                camera: z.optional(vec3),
                coordinateType: z.optional(z.string()),
                fromInstancer: z.optional(z.boolean()),
                generated: z.optional(vec3),
                normal: z.optional(vec3),
                object: z.optional(vec3),
                reflection: z.optional(vec3),
                uv: z.optional(vec3),
                window: z.optional(vec3),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<TexCoordSockets>
